import { useMemo, useState } from 'react';
import { Button } from 'reactstrap';
import styled from 'styled-components';
import * as _ from 'lodash';
import { useDataTransforms } from '../../utils/hooks';
import { asCurrencyFromCents } from '../../utils/formatters';
import TotalPrice from './TotalPrice';
import { featureFlags } from '../../utils/featureFlags';

const MONTHS_PER_YEAR = 12;

const priceKeys = {
  ANNUAL: 'pro_annual',
  MONTHLY: 'pro_monthly',
};

const PlanOption = styled(({ title, price, ...props }) => (
  <div {...props}>
    <h2>{title}</h2>
    <div>
      <span className="font-xxl">{asCurrencyFromCents(price)}</span>
    </div>
    <div>per editor / mo.</div>
  </div>
))`
  ${({ selected }) =>
    selected
      ? `
    outline: 3px solid var(--overyield-orange);
    `
      : `
      color: var(--text-muted-color);
    outline: 1px solid #ddd;
    transition: all 0.2s ease-in-out;
    &:hover {
      outline: 3px solid #ddd;
      color: var(--text-color);
    }
    `}
  padding: 20px;
  cursor: pointer;
  border-radius: 10px;
  width: 47%;
  margin: 0 2px;
`;

const CounterButton = styled(Button)`
  width: 2rem;
`;

const ListWithBullets = styled.ul`
  list-style-type: disc;
  margin-left: 1.25rem;
`;

const Counter = ({ count, setCount, min }) => {
  const increment = () => setCount(count + 1);
  const decrement = () => setCount(count - 1);
  return (
    <div className="d-flex align-items-center mb-4">
      <CounterButton outline onClick={decrement} disabled={count === min}>
        -
      </CounterButton>
      <div className="font-weight-bold mx-3">{count}</div>
      <CounterButton outline onClick={increment}>
        +
      </CounterButton>
    </div>
  );
};

const PlanContent = ({
  onCompleted,
  priceResult: { data: priceData, msg },
  initialValue,
}) => {
  const [selectedPrice, setSelectedPrice] = useState(
    initialValue?.stripePrice.lookupKey || priceKeys.ANNUAL,
  );
  const [seats, setSeats] = useState(initialValue?.seats || 1);

  const [priceMap = {}] = useDataTransforms(priceData, [
    (data) => _.keyBy(data.billingPrices.prices, 'lookupKey'),
  ]);
  // If this fails, there is something wrong with the prices in Stripe.
  const pricesValid = useMemo(
    () => Object.values(priceKeys).every((key) => priceMap[key]),
    [priceMap],
  );

  if (msg) return <div>{msg}</div>;
  if (!pricesValid) return <div>Error loading prices</div>;

  return (
    <div>
      <div className="mb-4">
        {featureFlags.FREE_TRIAL_LENGTH > 0 && (
          <>
            <h2 className="font-weight-bold mb-3">
              Start with a {featureFlags.FREE_TRIAL_LENGTH} day free trial to
              access:
            </h2>
            <ListWithBullets className="mb-4">
              <li>Unlimited farm designs</li>
              <li>Multi-scenario analysis</li>
              <li>Soil, climate, and suitability maps</li>
              <li>Economic and environmental projections</li>
              <li>50+ built-in crop datasets and more</li>
            </ListWithBullets>
            <div className="mb-4">
              Feel free to cancel anytime during the trial period if you change
              your mind.
            </div>
          </>
        )}
        <h2>Select Payment Plan</h2>
        <div className="d-flex justify-content-between">
          <PlanOption
            selected={selectedPrice === priceKeys.ANNUAL}
            onClick={() => setSelectedPrice(priceKeys.ANNUAL)}
            role="button"
            title="Annual Plan"
            price={priceMap.pro_annual.unitAmount / MONTHS_PER_YEAR}
          />
          <PlanOption
            selected={selectedPrice === priceKeys.MONTHLY}
            onClick={() => setSelectedPrice(priceKeys.MONTHLY)}
            role="button"
            title="Monthly Plan"
            price={priceMap.pro_monthly.unitAmount}
          />
        </div>
      </div>
      <div className="mb-4">
        <h2>Editors</h2>
        <Counter count={seats} setCount={setSeats} min={1} />
      </div>
      <TotalPrice seats={seats} stripePrice={priceMap[selectedPrice]} />
      <Button
        color="primary"
        block
        onClick={() => {
          const stripePrice = priceMap[selectedPrice];
          onCompleted({ seats, stripePrice });
        }}
      >
        Next: Review
      </Button>
    </div>
  );
};

export default PlanContent;
